import { LogoutReason, useAuthenticationStore } from "src/modules/authentication/useAuthenticationStore";

import type { ISDKRequestInterceptor, ISDKResponseInterceptor } from "@sol/sdk/SDKProvider";
import { userLoginRefreshTokenMutation } from "@sol/sdk/users/mutations/userLoginRefreshTokenMutation";

declare module "axios" {
    interface AxiosRequestConfig {
        // Add custom property to track retry attempts
        _attempt?: number;
        _sessionId?: string | null;
    }
}

/**
 * Add authorization header to each request if user is authenticated
 **/
export const authorizationInterceptor: ISDKRequestInterceptor = {
    kind: "request",
    onFullfilled: request => {
        // Add token to request if needed
        const { sessionId, token } = useAuthenticationStore.getState();

        request._sessionId = sessionId;

        if (token) {
            const isExternalAPICall = request.baseURL === process.env.API_URL;
            request.headers ??= {};

            if (isExternalAPICall) {
                // Use standard Authorization header for external API calls
                request.headers.Authorization = `Bearer ${token}`;
            } else {
                // Use custom header for internal Next.js routes to avoid conflict with Basic Auth
                request.headers["X-Auth-Token"] = `Bearer ${token}`;
            }
        }

        return request;
    },
};

const refreshTokenCache = new Map<string, Promise<any>>();

/**
 * Interceptor to refresh token on 401 response
 **/
export const refreshTokenInterceptor: ISDKResponseInterceptor = {
    kind: "response",
    onFullfilled: response => {
        // NOOP
        return response;
    },
    onRejected: async (error, instance) => {
        const originalRequest = error.config;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        const { refreshToken, sessionId, token } = useAuthenticationStore.getState();
        const requestSessionId = originalRequest._sessionId;

        const attempt = originalRequest._attempt ?? 0;

        if (requestSessionId !== sessionId) {
            return Promise.reject(error);
        }

        if (originalRequest.url === "/login/refresh_token") {
            // Avoid infinite loop if refresh token request itself fails
            return Promise.reject(error);
        }

        if (error.response?.status !== 401) {
            // Not an unauthorized error, reject as we are not concerned
            return Promise.reject(error);
        }

        if (attempt >= 1) {
            // Already attempted to refresh token once, avoid infinite loop
            useAuthenticationStore.getState().logout(LogoutReason.TokenExpired);
            return Promise.reject(error);
        }

        if (!token) {
            // No token available, cannot refresh
            return Promise.reject(error);
        }

        if (!refreshToken) {
            // No refresh token available, cannot refresh
            return Promise.reject(error);
        }

        if (!refreshTokenCache.has(refreshToken)) {
            // Do the refresh token
            refreshTokenCache.set(refreshToken, instance(userLoginRefreshTokenMutation({ refreshToken })));
        }

        try {
            const refreshResponse = await refreshTokenCache.get(refreshToken);

            // If successful, store new tokens and retry original request
            const { token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;

            refreshTokenCache.delete(refreshToken);

            if (useAuthenticationStore.getState().sessionId !== requestSessionId) {
                return Promise.reject(error);
            }

            useAuthenticationStore.getState().rotateTokens(newToken, newRefreshToken);

            originalRequest.headers ??= {};

            if (originalRequest.baseURL === process.env.API_URL) {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            } else {
                delete originalRequest.headers["Authorization"];
                originalRequest.headers["X-Auth-Token"] = `Bearer ${newToken}`;
            }

            // Retry original request that failed
            return instance({ ...originalRequest, _attempt: attempt + 1 });
        } catch (refreshError) {
            refreshTokenCache.delete(refreshToken);

            // If refresh fails with 401, logout user
            if (
                refreshError.response?.status === 401 &&
                useAuthenticationStore.getState().sessionId === requestSessionId
            ) {
                useAuthenticationStore.getState().logout(LogoutReason.TokenExpired);
            }

            return Promise.reject(refreshError);
        }
    },
};
